import { ConnectionQualityLevels } from "@minervaproject/avsubsystem-core";
import { isArray, round, uniq } from "lodash-es";

import { RootState } from "..";
import { PubsubVirtualUserState } from "../../shared/types/av-state";
import { MonitoringEventTypeType, UserStateStatsType } from "../../shared/types/monitoring";

import { createAppSelector } from "./base";

export const selectMonitoringEventCountByCourseIdAndUserIdAndType = createAppSelector(
  [
    (state: RootState) => state.monitoring.stats,
    (state: RootState, courseId: string | null) => courseId,
    (state: RootState, courseId: string | null, userId: string) => userId,
    (
      state: RootState,
      courseId: string | null,
      userId: string,
      eventType: MonitoringEventTypeType,
    ) => eventType,
  ],
  (stats, courseId, userId, eventType) => {
    if (!courseId || !stats[courseId] || !stats[courseId][eventType]) {
      return 0;
    }
    const data = stats[courseId][eventType];
    if (!isArray(data.userIds)) {
      return 0;
    }
    return data.userIds.filter((id) => id === userId).length;
  },
);

export const selectMonitoringEventCountByCourseIdAndTargetClassUserIdAndType = createAppSelector(
  [
    (state: RootState) => state.monitoring.stats,
    (state: RootState, courseId: string | null) => courseId,
    (state: RootState, courseId: string | null, targetClassUserId: string | null) =>
      targetClassUserId,
    (
      state: RootState,
      courseId: string | null,
      targetClassUserId: string | null,
      eventType: MonitoringEventTypeType,
    ) => eventType,
  ],
  (stats, courseId, targetClassUserId, eventType) => {
    if (!courseId || !targetClassUserId || !stats[courseId] || !stats[courseId][eventType]) {
      return 0;
    }
    const data = stats[courseId][eventType];
    if (!isArray(data.targetClassUserIds)) {
      return 0;
    }
    return data.targetClassUserIds.filter((id) => id === targetClassUserId).length;
  },
);

export const selectUserCountWithMonitoringEventsByCourseId = createAppSelector(
  [
    (state: RootState) => state.monitoring.stats,
    (state: RootState, courseId: string | undefined | null) => courseId,
  ],
  (stats, courseId) => {
    if (!courseId) {
      return 0;
    }
    const userIds = uniq(Object.values(stats[courseId] || {}).flatMap((x) => x.userIds));
    return userIds.length;
  },
);

const defaultUserStateStats: UserStateStatsType = {
  affectedUsers: [],
  percentAffectedUsers: null,
  formattedPercentage: "-",
};

const computeUserStateStats = (
  userData: Record<string, PubsubVirtualUserState>,
  nParticipants: number,
  filterFn: (userState: PubsubVirtualUserState) => boolean,
): UserStateStatsType => {
  if (!userData || !nParticipants) {
    return defaultUserStateStats;
  }
  const affectedUsers = Object.entries(userData)
    .filter(([, userState]) => filterFn(userState))
    .map(([classUserId]) => classUserId);
  const percentAffectedUsers = (affectedUsers.length / nParticipants) * 100;
  return {
    affectedUsers: uniq(affectedUsers),
    percentAffectedUsers,
    formattedPercentage: `${round(percentAffectedUsers, 2)}%`,
  };
};

export const selectPubsubConnectionStatsByCourseId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, courseId: string | null) =>
      selectUserCountWithMonitoringEventsByCourseId(state, courseId),
  ],
  (userData, nParticipants) =>
    computeUserStateStats(
      userData,
      nParticipants,
      (u) => u.pubsubWebsocketStatus === "disconnected",
    ),
);
export const selectMicErrorStatsByCourseId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, courseId: string | null) =>
      selectUserCountWithMonitoringEventsByCourseId(state, courseId),
  ],
  (userData, nParticipants) => computeUserStateStats(userData, nParticipants, (u) => !!u.micError),
);

export const selectCameraErrorStatsByCourseId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, courseId: string | null) =>
      selectUserCountWithMonitoringEventsByCourseId(state, courseId),
  ],
  (userData, nParticipants) =>
    computeUserStateStats(userData, nParticipants, (u) => !!u.cameraError),
);

export const selectHighPingStatsByCourseId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, courseId: string | null) =>
      selectUserCountWithMonitoringEventsByCourseId(state, courseId),
  ],
  (userData, nParticipants) =>
    computeUserStateStats(userData, nParticipants, (u) => !!u.pubsubPingRtt?.isHigh),
);

export const selectPoorConnectionStatsByCourseId = createAppSelector(
  [
    (state: RootState) => state.pubsub_virtual_class_state.users,
    (state: RootState, courseId: string | null) =>
      selectUserCountWithMonitoringEventsByCourseId(state, courseId),
  ],
  (userData, nParticipants) =>
    computeUserStateStats(
      userData,
      nParticipants,
      (u) => u.connectionQuality === ConnectionQualityLevels.POOR,
    ),
);

export const selectMonitoringStats = createAppSelector(
  [(state: RootState) => state.monitoring.stats],
  (stats) => stats,
);
